import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, EmptyState } from "@/components/app-shell";
import { Users, Plus, Search, Pencil, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useActiveCompany } from "@/hooks/use-active-company";


type Customer = {
  id: string;
  name: string;
  contact_person: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  org_number: string | null;
  notes: string | null;
  customer_type: string;
};

export const Route = createFileRoute("/_authenticated/customers")({
  head: () => ({
    meta: [
      { title: "Kunder | VERQO" },
      { name: "description", content: "Kunderegister for VERQO." },
    ],
  }),
  component: CustomersPage,
});

function CustomersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<Customer> | null>(null);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("*").order("name");
      if (error) throw error;
      return data as Customer[];
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("customers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["customers"] }); toast.success("Kunde slettet"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = customers.filter((c) =>
    [c.name, c.email, c.phone, c.city].some((v) => v?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <PageHeader
        title="Kunder"
        subtitle={`${customers.length} ${customers.length === 1 ? "kunde" : "kunder"}`}
        action={
          <button onClick={() => setEditing({})} className="btn btn-primary">
            <Plus className="h-4 w-4" /> Ny kunde
          </button>
        }
      />

      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          className="input-base pl-9"
          placeholder="Søk etter navn, e-post, telefon…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {customers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Ingen kunder ennå"
          description="Legg til din første kunde for å komme i gang."
          action={<button onClick={() => setEditing({})} className="btn btn-primary"><Plus className="h-4 w-4" /> Ny kunde</button>}
        />
      ) : (
        <div className="card divide-y divide-border">
          {filtered.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{c.name}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {c.customer_type === "company" ? "Firma" : "Privat"}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  {c.contact_person && <span>{c.contact_person}</span>}
                  {c.phone && <span>{c.phone}</span>}
                  {c.email && <span className="truncate">{c.email}</span>}
                  {c.city && <span>{c.city}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(c)} className="btn btn-ghost p-2" aria-label="Rediger">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => { if (confirm(`Slette ${c.name}?`)) del.mutate(c.id); }} className="btn btn-ghost p-2 text-destructive" aria-label="Slett">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Ingen treff.</div>
          )}
        </div>
      )}

      {editing && <CustomerModal initial={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function CustomerModal({ initial, onClose }: { initial: Partial<Customer>; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Partial<Customer>>(initial);
  const type = form.customer_type === "company" ? "company" : "private";
  const isEdit = Boolean(initial.id);
  const { companyId } = useActiveCompany();

  const save = useMutation({
    mutationFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Ikke innlogget");
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const payload = {
        name: form.name?.trim() || "",
        contact_person: form.contact_person || null,
        address: form.address || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        phone: form.phone || null,
        email: form.email || null,
        org_number: type === "company" ? form.org_number || null : null,
        notes: form.notes || null,
        customer_type: type,
      };
      if (!payload.name) throw new Error("Navn er påkrevd");
      if (isEdit && initial.id) {
        const { error } = await supabase.from("customers").update(payload).eq("id", initial.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("customers").insert({ ...payload, user_id: user.user.id, company_id: companyId });
        if (error) throw error;
      }
    },

    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success(isEdit ? "Kunde oppdatert" : "Kunde opprettet");
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const set = (k: keyof Customer) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-t-2xl bg-card p-5 sm:rounded-2xl sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? "Rediger kunde" : "Ny kunde"}</h2>
          <button onClick={onClose} className="btn btn-ghost p-2"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div>
            <label className="label-base">Kundetype</label>
            <div className="flex gap-2">
              {([["private", "Privatkunde"], ["company", "Firmakunde"]] as const).map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, customer_type: v }))}
                  className={`btn flex-1 ${type === v ? "btn-primary" : "btn-secondary"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div><label className="label-base">{type === "company" ? "Firmanavn *" : "Navn *"}</label><input className="input-base" value={form.name ?? ""} onChange={set("name")} required autoFocus /></div>
          <div><label className="label-base">Kontaktperson</label><input className="input-base" value={form.contact_person ?? ""} onChange={set("contact_person")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-base">Telefon</label><input className="input-base" value={form.phone ?? ""} onChange={set("phone")} /></div>
            <div><label className="label-base">E-post</label><input type="email" className="input-base" value={form.email ?? ""} onChange={set("email")} /></div>
          </div>
          <div><label className="label-base">{type === "company" ? "Fakturaadresse" : "Adresse"}</label><input className="input-base" value={form.address ?? ""} onChange={set("address")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-base">Postnr.</label><input className="input-base" value={form.postal_code ?? ""} onChange={set("postal_code")} /></div>
            <div><label className="label-base">Poststed</label><input className="input-base" value={form.city ?? ""} onChange={set("city")} /></div>
          </div>
          {type === "company" && (
            <div><label className="label-base">Org.nr.</label><input className="input-base" value={form.org_number ?? ""} onChange={set("org_number")} /></div>
          )}
          <div><label className="label-base">Notater</label><textarea className="input-base" rows={2} value={form.notes ?? ""} onChange={set("notes")} /></div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">Avbryt</button>
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>{isEdit ? "Lagre" : "Opprett"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
